/**
 * Change-password page. After a successful change, the backend rotates the
 * refresh token (other sessions are forced to re-auth), so we clear local
 * state and send the user back to the dashboard.
 */

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { authApi } from "@/lib/api";
import { ApiException } from "@/lib/api-client";

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => authApi.changePassword(current, next),
    onSuccess: () => navigate("/"),
    onError: (err) => setError(err instanceof ApiException ? err.message : "Failed to change password"),
  });

  const mismatch = next !== confirm;
  const tooShort = next.length > 0 && next.length < 12;
  const canSubmit = current && next && !mismatch && !tooShort;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    mut.mutate();
  }

  return (
    <div className="max-w-lg space-y-6">
      <PageHeader title="Change password" description="Choose a strong password (min 12 characters)." />
      <Card>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="cur">Current password</Label>
              <Input id="cur" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required autoComplete="current-password" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new">New password</Label>
              <Input id="new" type="password" value={next} onChange={(e) => setNext(e.target.value)} required autoComplete="new-password" />
              {tooShort && <p className="text-xs text-destructive">At least 12 characters.</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="conf">Confirm new password</Label>
              <Input id="conf" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required autoComplete="new-password" />
              {mismatch && confirm && <p className="text-xs text-destructive">Passwords don't match.</p>}
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => navigate("/")}>Cancel</Button>
              <Button type="submit" disabled={!canSubmit || mut.isPending}>
                {mut.isPending ? "Changing…" : "Change password"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
